// SF4 — Optimal Price Recommender.
//
// THIS NEVER CHANGES A PRICE. It states a case; the decision stays with the
// seller. Most rows are refusals, and that is the correct proportion — a wrong
// price costs margin on every unit sold, immediately, so the bar for naming one
// is deliberately high.
//
// What holding would earn is always shown beside any recommendation, so a
// seller matching down to a thin margin is never matching blind.
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { FiAlertTriangle, FiInfo } from 'react-icons/fi';

import AuthContext from '../../../../context/AuthContext';
import local from '../Pricing.module.css';
import { getRecommendations } from '../services/pricingApi';

const inr = new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 });

const STATUS_LABEL = {
  recommended: 'Move',
  no_elasticity: 'No measured response',
  inherited_only: 'Inherited only',
  direction_unclear: 'Evidence unclear',
  gain_too_small: 'Already near best',
  below_floor: 'Below your floor',
};

const Recommendations = () => {
  const authCtx = useContext(AuthContext);
  const token = authCtx?.token;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true); setError(null);
    try { setData(await getRecommendations(token)); }
    catch (err) { setError(err); setData(null); }
    finally { setLoading(false); }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const rows = data?.products || [];

  return (
    <section className={local.card}>
      <div className={local.cardHead}>
        <h2 className={local.cardTitle}>Where the evidence supports a price</h2>
        {data && (
          <span className={local.muted}>
            {data.recommended} of {data.considered} with enough evidence
          </span>
        )}
      </div>
      <p className={local.muted}>
        Nothing here changes a price. Each case is tested at the least
        favourable end of the measured response, so it has to survive the
        pessimistic reading rather than the flattering one.
      </p>

      {error && (
        <div className={`${local.banner} ${local.bannerError}`}>
          <FiAlertTriangle className={local.bannerIcon} />
          <div><strong>Could not load recommendations</strong><div>{error.message}</div></div>
        </div>
      )}

      {/* The ceilings are not being applied. That is load-bearing: a
          recommendation could sit above the price that holds the sale. */}
      {data && (
        <div className={`${local.banner} ${local.bannerWarn}`}>
          <FiInfo className={local.bannerIcon} />
          <div>
            <strong>Buy Box and competitor ceilings are not applied</strong>
            <div>
              Pricing SF3 owns them and has no offer history yet, so a
              recommendation here could sit above the price that actually holds
              the sale.
            </div>
          </div>
        </div>
      )}

      {!error && (
        <div className={local.tableScroll}>
          <table className={local.table}>
            <thead>
              <tr>
                <th>Product</th>
                <th className={local.numeric}>Now</th>
                <th className={local.numeric}>Suggested</th>
                <th className={local.numeric}>Holding earns</th>
                <th className={local.numeric}>Gain/day</th>
                <th>Verdict</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td className={local.emptyCell} colSpan={6}>
                  {loading ? 'Loading…' : 'No products with a measurable price response yet.'}
                </td></tr>
              ) : rows.map((row) => (
                <tr key={row.sku}>
                  <td>{row.sku}</td>
                  <td className={`${local.numeric} ${local.context}`}>
                    ₹{inr.format(row.current_price)}
                  </td>
                  <td className={local.numeric}>
                    {row.recommended_price
                      ? <strong>₹{inr.format(row.recommended_price)}</strong>
                      : <span className={local.blank}>—</span>}
                  </td>
                  <td className={`${local.numeric} ${local.context}`}>
                    {row.hold ? `₹${inr.format(row.hold.margin_per_day)}/day` : '—'}
                  </td>
                  <td className={local.numeric}>
                    {row.expected?.margin_gain_per_day
                      ? <span className={local.gain}>
                        +₹{inr.format(row.expected.margin_gain_per_day)}
                      </span>
                      : <span className={local.blank}>—</span>}
                  </td>
                  <td className={local.context} title={row.reason || ''}>
                    {STATUS_LABEL[row.status] || row.status}
                    {row.constraint && ` · capped by ${row.constraint}`}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {data?.notes?.length > 0 && (
        <ul className={local.noteList}>
          {data.notes.slice(0, 5).map((note) => (
            <li key={note}><FiInfo className={local.noteIcon} />{note}</li>
          ))}
        </ul>
      )}
    </section>
  );
};

export default Recommendations;
